import { ButtonDropdown, ButtonDropdownProps } from '@amzn/awsui-components-core';
import { ICellRendererParams } from 'ag-grid-community';
import React, { useCallback, useEffect, useRef } from 'react';
import { ForecastGridContext } from './LineItemIdCellRenderer';

const PO_ACTION_ITEMS: ButtonDropdownProps.Items = [
  { id: 'create-po', text: 'Create PO' },
  { id: 'show-pos', text: 'Show PO Requests' }
];

/**
 * Cell renderer for the PO column that displays a ButtonDropdown with actions:
 * - Create PO
 * - Show POs
 */
const POActionsCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  if (params.node.footer) {
    return null;
  }

  const lineItemId = params.data?.xpt_line_item_id;
  const context = params.context as ForecastGridContext | undefined;

  const handleItemClick = useCallback(
    (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
      if (!lineItemId) return;

      switch (event.detail.id) {
        case 'create-po':
          context?.onCreatePO?.(String(lineItemId));
          break;
        case 'show-pos':
          context?.onShowPOs?.(String(lineItemId));
          break;
      }
    },
    [lineItemId, context]
  );

  // Sync trigger width to CSS variable so the portal panel can use it
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const updateWidth = () => {
      const btn = wrapper.querySelector('button');
      if (btn) {
        document.documentElement.style.setProperty('--po-action-trigger-width', `${btn.offsetWidth}px`);
      }
    };

    const observer = new ResizeObserver(updateWidth);
    observer.observe(wrapper);
    updateWidth();

    return () => observer.disconnect();
  }, []);

  return (
    <div className="po-action-btn" ref={wrapperRef}>
      <ButtonDropdown
        items={PO_ACTION_ITEMS}
        variant="normal"
        expandToViewport
        onItemClick={handleItemClick}
        ariaLabel={`PO actions for line item ${lineItemId}`}
      >
        PO Actions
      </ButtonDropdown>
    </div>
  );
};

export default POActionsCellRenderer;
